/*
 OK FBI — Host Investigator Sound Pack + OBS Preset Layouts
 Two panels for the host, matched to the existing gold/gunmetal aesthetic.
*/
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import * as Pack from "@/lib/soundpack";
import { Play, RotateCcw, Volume2, VolumeX, Copy, Trash2, Save, Check, Pencil, Layers, Keyboard, Moon, Zap } from "lucide-react";
import { toast } from "sonner";

const SFX_META = [
  { key: "evidence_reveal",    label: "كشف دليل" },
  { key: "insight",            label: "اكتشاف معلومة مهمة" },
  { key: "file_open",          label: "فتح ملف" },
  { key: "play_recording",     label: "تشغيل تسجيل" },
  { key: "cctv_open",          label: "فتح كاميرا مراقبة" },
  { key: "fingerprint_result", label: "نتيجة فحص البصمة" },
  { key: "dna_result",         label: "نتيجة DNA" },
  { key: "voting_start",       label: "بدء التصويت" },
  { key: "voting_end",         label: "انتهاء التصويت" },
  { key: "answer_correct",     label: "إجابة صحيحة" },
  { key: "answer_wrong",       label: "إجابة خاطئة" },
  { key: "suspicion",          label: "لحظة شك" },
  { key: "contradiction",      label: "كشف تناقض" },
  { key: "accuse",             label: "توجيه الاتهام" },
  { key: "case_solved",        label: "تم حل القضية" },
  { key: "case_failed",        label: "فشل حل القضية" },
];

export function SoundPackPanel() {
  const [enabled, setEnabled] = useState(Pack.isEnabled());
  const [volume, setVol] = useState(Pack.getVolume());
  const [theme, setThemeState] = useState(Pack.getTheme());
  const [last, setLast] = useState(null);
  const fire = (key) => {
    Pack.fire(key);
    api.post("/session/sound-pack/fire", { key }).catch(() => {});
    setLast(key);
    setTimeout(() => setLast(null), 700);
  };
  const toggle = () => {
    const n = !enabled; setEnabled(n); Pack.setEnabled(n);
    api.patch("/session/sound-pack", { enabled: n }).catch(() => {});
  };
  const changeVol = (v) => {
    const val = Number(v); setVol(val); Pack.setVolume(val);
    api.patch("/session/sound-pack", { volume: val }).catch(() => {});
  };
  const changeTheme = (t) => {
    setThemeState(t); Pack.setTheme(t);
    api.patch("/session/sound-pack", { theme: t }).catch(() => {});
    // Preview: fire evidence_reveal on the new theme
    setTimeout(() => Pack.fire("evidence_reveal"), 60);
    toast.success(t === "noir" ? "تم اختيار: نوار سينمائي" : "تم اختيار: جنائي حديث");
  };
  return (
    <div className="panel p-4 border-metal" data-testid="soundpack-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="label-caps">مؤثرات المحقق</div>
        <button onClick={toggle} data-testid="soundpack-toggle"
          className={`text-[10px] px-2 py-0.5 border font-mono-ar ${enabled ? "border-emerald-700 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
          {enabled ? "تشغيل" : "إيقاف"}
        </button>
      </div>
      {/* Theme selector */}
      <div className="grid grid-cols-2 gap-1 mb-2" data-testid="soundpack-theme-selector">
        <button onClick={() => changeTheme("noir")} data-testid="soundpack-theme-noir"
          className={`py-1.5 border text-[11px] font-cairo flex items-center justify-center gap-1.5 transition-colors ${theme === "noir" ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal text-zinc-400 hover:border-gold/50 hover:text-white"}`}>
          <Moon size={11} /> نوار سينمائي
        </button>
        <button onClick={() => changeTheme("modern")} data-testid="soundpack-theme-modern"
          className={`py-1.5 border text-[11px] font-cairo flex items-center justify-center gap-1.5 transition-colors ${theme === "modern" ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal text-zinc-400 hover:border-gold/50 hover:text-white"}`}>
          <Zap size={11} /> جنائي حديث
        </button>
      </div>
      <div className="text-[9px] text-zinc-500 mb-1 font-mono-ar">حجم المؤثرات: {Math.round(volume * 100)}%</div>
      <input type="range" min="0" max="1" step="0.05" value={volume}
        onChange={(e) => changeVol(e.target.value)} data-testid="soundpack-volume"
        className="w-full accent-yellow-600 mb-3" disabled={!enabled} />
      <div className="grid grid-cols-2 gap-1">
        {SFX_META.map(s => (
          <button key={s.key} onClick={() => fire(s.key)} disabled={!enabled} data-testid={`soundpack-${s.key}`}
            className={`text-right px-2 py-1.5 border text-[11px] transition-colors ${last === s.key ? "border-gold bg-[rgba(197,160,89,0.15)] text-gold" : enabled ? "border-metal text-zinc-300 hover:border-gold/60 hover:text-gold" : "border-metal/40 text-zinc-600 cursor-not-allowed"}`}>
            <span className="font-cairo">{s.label}</span>
          </button>
        ))}
      </div>
      <div className="text-[9px] text-zinc-600 mt-2 leading-tight">مؤثرات دقيقة سينمائية بأسلوب واجهة تحقيق احترافية. جميع الأصوات معالجة داخلياً بلا ملفات خارجية · تبريد ذاتي يمنع التكرار السريع.</div>
    </div>
  );
}

// ============ OBS Preset Layouts ============
export function OverlayPresetsPanel() {
  const [presets, setPresets] = useState([]);
  const [active, setActive] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [newName, setNewName] = useState("");
  const load = useCallback(async () => {
    try {
      const { data: p } = await api.get("/session/overlay-presets");
      setPresets(p || []);
      try {
        const { data: state } = await api.get("/overlay/state");
        setActive(state?.active_preset || null);
      } catch {}
    } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const apply = async (id) => {
    try { await api.post(`/session/overlay-presets/${id}/apply`); setActive(id); toast.success("تم تفعيل الإعداد"); } catch {}
  };
  const saveCurrent = async () => {
    const nm = newName.trim() || `إعداد ${new Date().toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" })}`;
    try {
      const { data: state } = await api.get("/overlay/state");
      await api.post("/session/overlay-presets", { name: nm, config: state.config });
      setNewName(""); toast.success("تم حفظ الإعداد"); load();
    } catch {}
  };
  const dup = async (p) => {
    try { await api.post("/session/overlay-presets", { name: `${p.name} - نسخة`, config: p.config }); load(); } catch {}
  };
  const del = async (id) => { try { await api.delete(`/session/overlay-presets/${id}`); load(); } catch {} };
  const beginRename = (p) => { setRenaming(p.id); setRenameValue(p.name); };
  const commitRename = async (id) => {
    if (!renameValue.trim()) { setRenaming(null); return; }
    try { await api.patch(`/session/overlay-presets/${id}`, { name: renameValue.trim() }); setRenaming(null); load(); } catch {}
  };

  return (
    <div className="panel p-4 border-metal" data-testid="presets-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Layers size={14} className="text-gold" /><div className="label-caps">إعدادات البث المحفوظة</div></div>
        <div className="text-[9px] text-zinc-600 font-mono-ar">OBS PRESETS · {presets.length}</div>
      </div>
      <div className="flex gap-1 mb-3">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="اسم الإعداد الجديد..." data-testid="preset-new-name"
          className="flex-1 bg-black/40 border border-metal px-2 py-1.5 text-[11px] text-white outline-none focus:border-gold" />
        <button onClick={saveCurrent} data-testid="preset-save-current" className="btn-primary text-[11px] px-3 py-1 flex items-center gap-1"><Save size={11} /> حفظ الحالي</button>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {presets.map(p => (
          <div key={p.id} className={`flex items-center justify-between p-1.5 border ${active === p.id ? "border-gold bg-[rgba(197,160,89,0.08)]" : "border-metal/50"} text-xs`} data-testid={`preset-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              {renaming === p.id ? (
                <input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commitRename(p.id); if (e.key === "Escape") setRenaming(null); }}
                  data-testid={`preset-rename-input-${p.id}`}
                  className="w-full bg-black/40 border border-gold px-1.5 py-0.5 text-xs text-white outline-none" />
              ) : (
                <>
                  <span className="font-cairo text-white truncate">{p.name}</span>
                  {p.is_default && <span className="text-[9px] text-zinc-500 mr-2 font-mono-ar">DEFAULT</span>}
                </>
              )}
            </div>
            <div className="flex gap-0.5">
              <button onClick={() => apply(p.id)} data-testid={`preset-apply-${p.id}`} className={`text-[10px] px-2 py-0.5 border ${active === p.id ? "border-gold text-gold" : "border-metal text-zinc-300 hover:border-gold hover:text-gold"}`}>تفعيل</button>
              <button onClick={() => beginRename(p)} data-testid={`preset-rename-${p.id}`} title="إعادة تسمية" className="w-6 h-6 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><Pencil size={9} /></button>
              <button onClick={() => dup(p)} data-testid={`preset-dup-${p.id}`} title="نسخ" className="w-6 h-6 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><Copy size={9} /></button>
              {!p.is_default && <button onClick={() => del(p.id)} data-testid={`preset-del-${p.id}`} title="حذف" className="w-6 h-6 border border-red-900 hover:border-red-500 text-red-400 flex items-center justify-center"><Trash2 size={9} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Difficulty Ramp ============
export function DifficultyRampPanel() {
  const [state, setState] = useState({ ramp: { mode: "off" }, effective_for_current: null });
  const load = useCallback(async () => {
    try { const { data } = await api.get("/session/difficulty-ramp"); setState(data); } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const set = async (mode) => { try { await api.post("/session/difficulty-ramp", { mode }); load(); toast.success(mode === "off" ? "تم إيقاف التصاعد" : mode === "auto" ? "تفعيل التصاعد التلقائي" : "الوضع المخصص"); } catch {} };
  const mode = state.ramp?.mode || "off";
  const modes = [{ k: "off", l: "إيقاف" }, { k: "auto", l: "تلقائي" }, { k: "custom", l: "مخصص" }];
  return (
    <div className="panel p-4 border-metal" data-testid="ramp-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="label-caps">تصاعد مستوى الصعوبة</div>
        <div className="text-[9px] text-zinc-600 font-mono-ar">DIFFICULTY RAMP</div>
      </div>
      <div className="grid grid-cols-3 gap-1 mb-2">
        {modes.map(m => (
          <button key={m.k} onClick={() => set(m.k)} data-testid={`ramp-${m.k}`}
            className={`py-1.5 border text-[11px] font-cairo transition-colors ${mode === m.k ? "border-gold text-gold bg-[rgba(197,160,89,0.08)]" : "border-metal text-zinc-400 hover:border-gold/50 hover:text-white"}`}>
            {m.l}
          </button>
        ))}
      </div>
      {mode === "auto" && (
        <div className="text-[10px] text-zinc-400 mb-1">
          التسلسل التلقائي: <span className="text-gold font-mono-ar">متوسط → متوسط → صعب → صعب → صعب</span>
        </div>
      )}
      {state.effective_for_current && (
        <div className="text-[10px] text-emerald-400 mt-1" data-testid="ramp-effective">
          الصعوبة المطبّقة على القضية الحالية: <span className="font-mono-ar">{state.effective_for_current.toUpperCase()}</span>
        </div>
      )}
      <div className="text-[9px] text-zinc-600 mt-2 leading-tight">التصاعد لا يغيّر الجاني أبداً. المضيف يستطيع دائماً التجاوز اليدوي قبل بدء أي قضية.</div>
    </div>
  );
}

// ============ Overlay sound-pack listener (fires stingers on overlay page) ============
export function useOverlaySfxListener(state) {
  const [seen, setSeen] = React.useState(null);
  React.useEffect(() => {
    const last = state?.last_sfx;
    if (!last) return;
    if (seen && seen.at === last.at) return;
    setSeen(last);
    const pack = state?.sound_pack;
    if (pack && pack.enabled === false) return;
    Pack.setVolume(pack?.volume ?? 0.15);
    if (pack?.theme) Pack.setTheme(pack.theme);
    Pack.fire(last.key);
  }, [state?.last_sfx?.at, state?.sound_pack?.enabled, state?.sound_pack?.volume, state?.sound_pack?.theme]);
}

// ============ Application-wide Keyboard Shortcuts — REMOVED ============
// (production correction pass) — the previous OBS-preset hotkey system was
// removed because it caused accidental gameplay triggers when users typed
// numbers inside forms/inputs. The exports below are kept as safe no-ops so
// downstream imports do not break; they intentionally do nothing.

export function useOverlayHotkeys() {
  // No-op: no global keydown listeners are attached anywhere in the app.
  React.useEffect(() => { /* removed */ }, []);
}

const HOTKEY_LABELS = {}; // retained empty for compatibility

// ============ Hotkeys config panel — REMOVED ============
export function HotkeysPanel() {
  return (
    <div className="panel p-4 border-metal text-xs font-cairo text-neutral-400"
         data-testid="hotkeys-panel-removed" dir="rtl">
      اختصارات لوحة المفاتيح غير مستخدمة في التطبيق.
    </div>
  );
}

// Legacy _LegacyHotkeysPanel_removed function fully deleted (item 17
// legacy cleanup). No dead code remains.
